import React from "react";
import { useSubscriptionHome } from "../../context/SubscriptionContextHome";

// Badge component
const Badge = ({ price, per }) => (
  <div className="relative w-20 h-20">
    <img
      src="https://cdn.acsdev.in/Rentastic/ultra.png"
      alt="badge"
      className="w-full h-full object-contain -mt-4"
    />
    <div className="absolute inset-0 flex items-center justify-center -mt-4 text-center">
      <span className="text-white text-sm font-semibold leading-tight">
        {price}
        <br />
        <span className="text-xs font-light">{per}</span>
      </span>
    </div>
  </div>
);

const SubscriptionHome = () => {
  const { plans, loading } = useSubscriptionHome();

  if (loading) {
    return (
      <div className="text-center py-10 text-gray-600">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        <p className="mt-2">Loading subscription plans...</p>
      </div>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <div className="text-center py-10 text-gray-600">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 max-w-md mx-auto">
          <h3 className="text-gray-800 font-semibold mb-2">
            No Plans Available
          </h3>
          <p className="text-gray-600 text-sm">
            No subscription plans are available at the moment. Please check back later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
          Our Subscription Plans
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Discover the perfect plan that fits your needs and budget
        </p>
      </div>

      {/* Responsive Grid Container */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
        {plans.map((plan, index) => (
          <div
            key={plan.id}
            className="bg-white rounded-2xl shadow-lg p-6 flex flex-col relative transition-all duration-300 hover:shadow-2xl hover:scale-105 hover:z-10 group"
            style={{ zIndex: 1 }}
            role="article"
            aria-labelledby={`plan-${plan.id}-title`}
          >
            {/* Badge */}
            <div className="absolute top-2 right-2 z-20">
              <Badge price={plan.price} per={plan.per} />
            </div>

            {/* Plan Title */}
            <h3
              id={`plan-${plan.id}-title`}
              className="text-xl font-semibold text-gray-800 mb-6 text-center pr-16"
            >
              {plan.name}
            </h3>

            {/* Price Display */}
            <div className="text-center mb-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
              <div className="text-3xl font-bold text-gray-800">
                ${plan.price}
              </div>
              <div className="text-sm text-gray-600">
                per {plan.per.toLowerCase()}
              </div>
            </div>

            {/* Features List */}
            <div className="flex-grow">
              <h4 className="text-sm font-medium text-gray-700 mb-4">
                What's included:
              </h4>
              <ul className="space-y-3 text-gray-600 text-sm" role="list">
                {plan.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span
                      className="text-green-600 font-bold text-base leading-none mt-0.5 flex-shrink-0"
                      aria-hidden="true"
                    >
                      ✓
                    </span>
                    <span className="leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Status Badge (if needed) */}
            {/* {plan.status && (
              <div className="mt-6 text-center">
                <span className="inline-block px-3 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                  {plan.status}
                </span>
              </div>
            )} */}

            {/* Hover Effect Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-green-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl pointer-events-none"></div>
          </div>
        ))}
      </div>

      {/* Additional Info */}
      <div className="text-center mt-12">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 max-w-4xl mx-auto">
          <h3 className="text-blue-900 font-semibold mb-2 text-lg">
            Need Help Choosing?
          </h3>
          <p className="text-blue-800 text-sm leading-relaxed">
            Contact our support team if you need assistance selecting the right plan for your needs.
          </p>
        </div>
      </div>

      {/* Features Comparison Note */}
      {/* <div className="text-center mt-8">
        <p className="text-gray-500 text-sm">
          Compare features and find the plan that works best for you
        </p>
      </div> */}
    </div>
  );
};

export default SubscriptionHome;
